import type { ReactElement } from "react";

import type { Results } from "./generated/openapi";

export type FormSectionId = string;
export type ResultsPath = string;

export type SectionValues = Record<string, string>;

// Data Entry Section Types
export interface HeadingSubsection {
  type: "heading";
  title: string;
}

export interface MessageSubsection {
  type: "message";
  variant?: "dimmed";
  message: string;
}

export interface RadioSubsectionOption {
  value: string;
  /** Label for data entry form view */
  label: string | ReactElement;
  /** Short label for differences view */
  short_label: string;
  autoFocusInput?: boolean;
}

export interface RadioSubsection {
  type: "radio";
  /** Title to display above the radio buttons */
  title?: string | ReactElement;
  /** Description to display above the radio buttons */
  description?: string;
  /** Short title for differences view */
  short_title: string;
  error: string;
  path: ResultsPath;
  options: RadioSubsectionOption[];
}

export interface CheckboxesSubsectionOption {
  path: ResultsPath;
  /** Label for data entry form view */
  label: string | ReactElement;
  /** Short label for differences view */
  short_label: string;
  autoFocusInput?: boolean;
}

export interface CheckboxesSubsection {
  type: "checkboxes";
  /** Title to display above the checkboxes */
  title?: string | ReactElement;
  /** Description to display above the checkboxes */
  description?: string;
  /** Short title for differences view */
  short_title: string;
  /** Path in results object that will indicate an error */
  error_path: ResultsPath;
  /** Error message to be shown when there is an error for error_path */
  error_message: string;
  options: CheckboxesSubsectionOption[];
}

export interface InputGridSubsectionRow {
  code?: string;
  path: ResultsPath;
  title: string;
  isDisabled?: boolean;
  isTotal?: boolean;
  isListTotal?: boolean;
  addSeparator?: boolean;
  autoFocusInput?: boolean;
}

export interface InputGridSubsection {
  type: "inputGrid";
  headers: [string, string, string];
  zebra?: boolean;
  rows: InputGridSubsectionRow[];
}

export type DataEntrySubsection =
  | HeadingSubsection
  | MessageSubsection
  | RadioSubsection
  | InputGridSubsection
  | CheckboxesSubsection;

export function isCheckboxesSubsection(subsection: DataEntrySubsection): subsection is CheckboxesSubsection {
  return subsection.type === "checkboxes";
}

export interface EqualToCondition {
  path: ResultsPath;
  equal_to: string;
}

export interface DataEntrySection {
  id: FormSectionId;
  title: string;
  short_title: string;
  disabled_when?: EqualToCondition;
  sectionNumber?: string;
  subsections: DataEntrySubsection[];
}

export type DataEntryModel = Results["model"];

// All models, includes exhaustive check if all models are defined
export const dataEntryModelValues = (<T extends DataEntryModel[]>(
  models: [Exclude<DataEntryModel, T[number]>] extends [never] ? T : never,
) => models)(["DSOFirstSession", "CSOFirstSession", "DSONextSession", "CSONextSession", "GSB"]);

export type DataEntryStructure = DataEntrySection[];

export type DataEntryResults = object;
